import {
  devicesChannelsFlat,
  getDevicesChannelsFlatQueryKey,
  getDevicesListQueryKey,
  useDeviceContractByMeter as useGenDeviceContractByMeter,
  useDevicesChannels as useGenDevicesChannels,
  useDevicesChannelsFlat as useGenDevicesChannelsFlat,
  useDevicesFeeders as useGenDevicesFeeders,
  useDevicesGet as useGenDevicesGet,
  useDevicesList as useGenDevicesList,
  useDevicesRegions as useGenDevicesRegions,
  useDevicesUpdate as useGenDevicesUpdate,
  useDevicesFeederConnections as useGenFeederConnections,
  useUpdateFeeder as useGenUpdateFeeder,
} from "@shared/api/generated/devices/devices";
import { useQueryClient } from "@tanstack/react-query";

export type {
  Channel,
  ChannelMeterRow,
  DeviceObject,
  DeviceObjectUpdateRequest,
  DevicesChannelsFlatParams,
  Feeder,
  FeederUpdateRequest,
  Region,
} from "@shared/api/generated/model";

// ── Queries ─────────────────────────────────────────────────────────────────────

export const useRegions = () => useGenDevicesRegions({ query: { staleTime: 60_000 } });

export const useObjects = (region?: number) =>
  useGenDevicesList(region !== undefined ? { region } : undefined, {
    query: { staleTime: 60_000 },
  });

export const useFeeders = (region?: number, object?: number) => {
  const enabled = region !== undefined && object !== undefined;
  return useGenDevicesFeeders(region ?? 0, object ?? 0, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useChannels = (region?: number, object?: number, feeder?: number) => {
  const enabled = region !== undefined && object !== undefined && feeder !== undefined;
  return useGenDevicesChannels(region ?? 0, object ?? 0, feeder ?? 0, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useChannelsFlat = (object?: number, meter?: string, limit = 500) => {
  const params: { object?: number; meter?: string; limit?: number } = { limit };
  if (object) params.object = object;
  if (meter) params.meter = meter;
  return useGenDevicesChannelsFlat(params, { query: { staleTime: 60_000 } });
};

export const getChannelsFlat = (params: { object?: number; meter?: string; limit?: number }) =>
  devicesChannelsFlat(params);

// ── Mutations ───────────────────────────────────────────────────────────────────

export const useUpdateObject = () => {
  const qc = useQueryClient();
  return useGenDevicesUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getDevicesListQueryKey() });
      },
    },
  });
};

export const useUpdateFeeder = () => {
  const qc = useQueryClient();
  return useGenUpdateFeeder({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({
          queryKey: getDevicesChannelsFlatQueryKey(),
        });
      },
    },
  });
};

// ── Detail queries ──────────────────────────────────────────────────────────────

export const useDeviceObject = (region?: number, object?: number) => {
  const enabled = region !== undefined && object !== undefined;
  return useGenDevicesGet(region ?? 0, object ?? 0, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useFeederConnections = (regionId?: number, objectId?: number, feederId?: number) => {
  const enabled = regionId !== undefined && objectId !== undefined && feederId !== undefined;
  return useGenFeederConnections(regionId ?? 0, objectId ?? 0, feederId ?? 0, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useContractByMeter = (
  regionId?: number,
  objectId?: number,
  feederId?: number,
  meterNumber?: string,
) => {
  const enabled = Boolean(regionId && objectId && feederId && meterNumber);
  return useGenDeviceContractByMeter(
    regionId ?? 0,
    objectId ?? 0,
    feederId ?? 0,
    meterNumber ?? "",
    { query: { enabled, staleTime: 60_000 } },
  );
};
